import React, { useState } from 'react'
import {SquareCheck as CheckSquare, Square, ChevronDown, ChevronUp, ExternalLink, Target, Zap, BookOpen, TrendingUp, AlertCircle, Clock, CheckCircle2} from 'lucide-react'

interface CheckItem {
  id: string
  text: string
  detail?: string
  done: boolean
  tag?: string
}

interface Section {
  id: string
  step: number
  badge: string
  title: string
  subtitle: string
  timeline: string
  timelineColor: string
  icon: React.ReactNode
  goal: string
  items: CheckItem[]
  note?: string
  noteType?: 'info' | 'warning' | 'success'
  expanded: boolean
}

const STORAGE_KEY = 'ongu-marketing-2026-checks'

function loadChecks(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function saveChecks(checks: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(checks))
}

const MarketingPlanPage: React.FC = () => {
  const [checks, setChecks] = useState<Record<string, boolean>>(loadChecks)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    step1: true,
    step2: true,
    step3: true,
    step4: false,
    step5: false,
  })

  const toggle = (id: string) => {
    const next = { ...checks, [id]: !checks[id] }
    setChecks(next)
    saveChecks(next)
  }

  const toggleSection = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const sections: Section[] = [
    {
      id: 'step1',
      step: 1,
      badge: '本週',
      title: 'Google Merchant Center 接通',
      subtitle: '搜尋「咖啡豆」→ 出現在 Shopping 商品卡',
      timeline: '目標：7天內上線',
      timelineColor: 'text-emerald-600 dark:text-emerald-400',
      icon: <Zap size={20} />,
      goal: '最快出現在第一頁的方法。不需等 SEO 爬升，2週內可達成。',
      items: [
        {
          id: 's1-1',
          text: '建立 Google Merchant Center 帳號',
          detail: '前往 merchants.google.com → 綁定 ongucoffee.com 網域 → 驗證網域所有權',
          done: false,
        },
        {
          id: 's1-2',
          text: '建立商品 Feed（手動 or XML）',
          detail: '最低需要欄位：id, title, description, link, image_link, price, availability, brand, condition',
          done: false,
          tag: '技術',
        },
        {
          id: 's1-3',
          text: '商品 title 加入 SEO 關鍵詞',
          detail: '前台顯示名稱不變（業主看不到）。Feed title 改為：「【精品咖啡豆】衣索比亞 Chilaka BR 手沖咖啡豆」→ 吃到「精品咖啡豆」「手沖咖啡豆」',
          done: false,
          tag: '關鍵',
        },
        {
          id: 's1-4',
          text: '商品 description 加入搜尋詞',
          detail: '加入：「精品咖啡豆 手沖咖啡豆推薦 自家烘焙 ONGU Studio 信義區」等關鍵字',
          done: false,
        },
        {
          id: 's1-5',
          text: '設定 Google Ads Shopping 廣告活動',
          detail: '即使預算小（每日 200–500）也可讓商品曝光在 Shopping 區。出價策略：最大化點擊次數（初期）',
          done: false,
        },
        {
          id: 's1-6',
          text: '在 Deno Function 或後端自動生成 product feed XML',
          detail: '路徑建議：/product-feed.xml → Google Merchant 定期抓取。可用 Deno Function 動態生成最新商品資料',
          done: false,
          tag: '技術',
        },
        {
          id: 's1-7',
          text: '驗證商品狀態：通過 Google 審核',
          detail: '商品審核通常 1–3 個工作天。常見不通過原因：缺少 GTIN / 圖片不符規格（最小 100x100px）',
          done: false,
        },
      ],
      note: '⚡ 這是最快出現在「咖啡豆」第一頁的方法。前台完全不動，業主看不到任何改變。',
      noteType: 'success',
      expanded: true,
    },
    {
      id: 'step2',
      step: 2,
      badge: '本週',
      title: '/shop/beans FAQ 搜尋型問句優化',
      subtitle: 'AI Overview 引用 → 第一頁最上方',
      timeline: '目標：3天內完成',
      timelineColor: 'text-blue-600 dark:text-blue-400',
      icon: <Target size={20} />,
      goal: 'Google AI Overview 抓的是：FAQ schema + 結構化問句 + E-E-A-T。現在的 FAQ 太像說明書，要改成真實搜尋語句。',
      items: [
        {
          id: 's2-1',
          text: '把現有 FAQ 問題改成搜尋型問句',
          detail: '❌ 舊版：「Q1｜商業合作的最低訂購量是多少？」\n✅ 新版：「精品咖啡豆推薦哪個好？怎麼選適合自己的咖啡豆？」',
          done: false,
          tag: '關鍵',
        },
        {
          id: 's2-2',
          text: '加入以下 5 個 FAQ 問句（高搜尋意圖）',
          detail: '① 咖啡豆推薦哪種比較好喝？\n② 手沖咖啡豆和義式咖啡豆有什麼不同？\n③ 精品咖啡豆怎麼保存才新鮮？\n④ 第一次買咖啡豆要選什麼？\n⑤ 台北哪裡可以買到自家烘焙精品咖啡豆？',
          done: false,
          tag: '內容',
        },
        {
          id: 's2-3',
          text: '確認 FAQPage Schema 正確注入 prerender',
          detail: '打開 prerender 後的頁面原始碼，確認 <script type="application/ld+json"> 中有 FAQPage schema，且所有 Q&A 文字都在 HTML 裡（不是 JS 後才渲染）',
          done: false,
          tag: '技術',
        },
        {
          id: 's2-4',
          text: '在 FAQ 答案中自然帶入關鍵詞',
          detail: '每個答案結尾加一句導流：「→ 瀏覽 ONGU Studio 精品咖啡豆系列」並附上 /zh/shop/beans 的內鏈',
          done: false,
        },
        {
          id: 's2-5',
          text: '補一段「咖啡豆推薦」介紹文字（150字）',
          detail: '在 Coffee Bean Guide 上方加：「如果你正在尋找咖啡豆推薦，ONGU Studio 提供多款精品現烘咖啡豆，包含衣索比亞、哥倫比亞等產區...」\n這段讓 Google 同時吃到「咖啡豆推薦」這個大詞。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 's2-6',
          text: '送出 Search Console 重新抓取',
          detail: '改完後到 Google Search Console → URL 檢查 → 要求重新索引 /zh/shop/beans',
          done: false,
        },
      ],
      note: '💡 業主的極簡排版完全不受影響。FAQ accordion 可以保持收合狀態，關鍵是 HTML 裡的文字必須存在（prerender 已確認可行）。',
      noteType: 'info',
      expanded: true,
    },
    {
      id: 'step3',
      step: 3,
      badge: '第2–4週',
      title: '一篇真正的「咖啡豆推薦」長文',
      subtitle: '/lab/guide/coffee-beans-recommend → 全程指向 /shop/beans',
      timeline: '目標：第2週發布',
      timelineColor: 'text-purple-600 dark:text-purple-400',
      icon: <BookOpen size={20} />,
      goal: '不是 120 篇，就這 1 篇。但要做到 2000 字以上、結構完整、每個段落都有指回 /shop/beans 的理由。這 1 篇的 SEO 效果比 10 篇平庸文章強 10 倍。',
      items: [
        {
          id: 's3-1',
          text: 'URL 確定：/zh/lab/guide/coffee-beans-recommend',
          detail: 'Slug 用英文 kebab-case，不用中文。這個 URL 本身就是一個 SEO 訊號。',
          done: false,
        },
        {
          id: 's3-2',
          text: 'H1 標題：「咖啡豆推薦指南：2026 年精品咖啡豆怎麼選？」',
          detail: '命中三個詞：咖啡豆推薦 / 精品咖啡豆 / 咖啡豆怎麼選。H1 全站唯一，不重複。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 's3-3',
          text: '文章架構（2000+ 字）',
          detail: 'H2①：你適合哪種咖啡豆？（依風味選）\nH2②：手沖咖啡豆推薦（命中「手沖咖啡豆」）\nH2③：義式咖啡豆推薦（命中「義式咖啡豆」）\nH2④：精品咖啡豆 vs 一般咖啡豆差別\nH2⑤：ONGU Studio 現烘咖啡豆推薦（導購）\nH2⑥：FAQ（4–6 個搜尋型問句）',
          done: false,
          tag: '結構',
        },
        {
          id: 's3-4',
          text: '每個 H2 段落結尾加導流 CTA',
          detail: '固定格式：「→ 查看 ONGU Studio [產品名稱]」連結到 /zh/shop/beans 或具體商品頁',
          done: false,
        },
        {
          id: 's3-5',
          text: '文章最後有明確的「推薦購買」區塊',
          detail: '列出 3 款 ONGU 咖啡豆 + 各自風味描述 + 直接加入購物車連結。這是轉換率最高的區塊。',
          done: false,
          tag: '關鍵',
        },
        {
          id: 's3-6',
          text: '加入 BlogPosting + FAQPage Schema',
          detail: 'BlogPosting：author / datePublished / headline / image\nFAQPage：5 個搜尋型問句的結構化標記',
          done: false,
          tag: '技術',
        },
        {
          id: 's3-7',
          text: 'Meta Title：「咖啡豆推薦 2026｜精品咖啡豆選購指南 - ONGU Studio」',
          detail: '55 字以內。命中：咖啡豆推薦 / 精品咖啡豆 / ONGU Studio 品牌詞。',
          done: false,
        },
        {
          id: 's3-8',
          text: 'Meta Description（150字）',
          detail: '「還在選咖啡豆嗎？ONGU Studio 整理了 2026 年精品咖啡豆推薦指南，依手沖、義式、冷萃分類，找到最適合你的風味。信義區職人自家烘焙，新鮮直送。」',
          done: false,
        },
        {
          id: 's3-9',
          text: '設定 og:image（1200x630）',
          detail: '用烘豆或咖啡豆近景照，加上文字疊加：「2026 咖啡豆推薦」。這決定 LINE / FB 分享時的視覺。',
          done: false,
        },
        {
          id: 's3-10',
          text: '從 /shop/beans 的 Guide 區塊加入此文章連結',
          detail: '在 Coffee Bean Guide 下方加：「→ 閱讀完整選豆指南」連結到這篇文章。形成雙向內鏈。',
          done: false,
          tag: '內鏈',
        },
        {
          id: 's3-11',
          text: '發布後送出 Search Console 索引請求',
          detail: 'Google Search Console → URL 檢查 → 輸入 /zh/lab/guide/coffee-beans-recommend → 要求重新索引',
          done: false,
        },
      ],
      note: '📌 這篇文章的核心策略：不是寫給讀者看的，是寫給 Google 的一封信，主題是「ONGU Studio = 咖啡豆推薦的最佳來源」。',
      noteType: 'info',
      expanded: true,
    },
    {
      id: 'step4',
      step: 4,
      badge: '持續進行',
      title: 'Google Map 信義店恢復第一頁',
      subtitle: '「信義區咖啡廳」門店來客流量',
      timeline: '目標：4週內回到第一頁',
      timelineColor: 'text-orange-600 dark:text-orange-400',
      icon: <TrendingUp size={20} />,
      goal: '門店來客 80% 來自 Google Map，不是網站。排名掉是因為不相關照片太多，訊號變弱。',
      items: [
        {
          id: 's4-1',
          text: '清理 Google Business 不相關照片',
          detail: '只保留：咖啡 / 吧台 / 店內空間 / 手沖 / 烘豆 / 咖啡豆。刪除：人物聚會、活動、非咖啡照片',
          done: false,
          tag: '優先',
        },
        {
          id: 's4-2',
          text: '每週上傳 5–10 張新照片',
          detail: '類型：手沖過程、烘豆機、咖啡豆近景、門市空間、今日豆單板。Google 很吃「持續更新」訊號。',
          done: false,
        },
        {
          id: 's4-3',
          text: '每週發 2 篇 Google Post',
          detail: '內容：本週豆單 / 烘焙日誌 / 活動通知。這是 Map 排名直接因子。',
          done: false,
        },
        {
          id: 's4-4',
          text: '建立「掃 QR 留評論」系統',
          detail: '製作一張小卡 / 桌面立牌：「喜歡今天的咖啡？掃碼留言，感謝您」→ 直接連到 Google 評論連結。目標：每週 +5 則',
          done: false,
          tag: '關鍵',
        },
        {
          id: 's4-5',
          text: '確認 Google Business 分類設定',
          detail: '主分類：Coffee shop\n附加分類：Coffee roaster（這個非常重要！讓你吃到「自家烘焙」搜尋流量）',
          done: false,
        },
        {
          id: 's4-6',
          text: '/store/xinyi 頁面補 Local SEO 關鍵詞',
          detail: '加入：信義區精品咖啡 / 信義區咖啡豆 / 信義區自家烘焙咖啡 等 H2 標題與段落文字',
          done: false,
          tag: '技術',
        },
      ],
      note: '📍 目標評論數：現在 → 200+。評論是 Local SEO 最大權重因子之一。',
      noteType: 'info',
      expanded: false,
    },
    {
      id: 'step5',
      step: 5,
      badge: '第2–3個月',
      title: 'B2B 數位引流漏斗',
      subtitle: '廣告 → 試豆申請 → 成交',
      timeline: '目標：3個月 20 家 B2B 客戶',
      timelineColor: 'text-rose-600 dark:text-rose-400',
      icon: <Target size={20} />,
      goal: '不靠掃街。用精準廣告投放 500m-1km 信義區商辦，讓有需求的人主動填表。',
      items: [
        {
          id: 's5-1',
          text: '✅ /zh/wholesale 頁面已嵌入試豆申請表單',
          detail: '✅ 已完成：表單包含姓名/公司/電話/業態/月用量/補充說明。\n送出後自動存入後台 b2b_leads 資料庫，並寄送 Email 通知業主。\n後台路徑：/admin/b2b-leads 可查看所有詢問',
          done: true,
          tag: '關鍵',
        },
        {
          id: 's5-2',
          text: '製作 B2B 試豆包',
          detail: '3 款咖啡豆 × 200g。附：風味說明卡 + 報價單 + 聯絡方式。成本約 300–500 元。這是轉換神器。',
          done: false,
        },
        {
          id: 's5-3',
          text: 'Meta Ads 精準投放（500m–1km 信義商辦）',
          detail: '受眾：信義區半徑 1km / 餐飲業主興趣 / 辦公室採購相關\n廣告文案：「信義區自家烘焙咖啡豆，提供餐廳/辦公室免費試豆申請」\n導向：/zh/wholesale 的試豆表單',
          done: false,
        },
        {
          id: 's5-4',
          text: 'Google Ads 關鍵字廣告（B2B 詞）',
          detail: '投放詞：咖啡豆批發 / 營業用咖啡豆 / 咖啡豆供應商 / 餐廳咖啡豆\n每日預算：500–800\n導向：/zh/wholesale',
          done: false,
        },
        {
          id: 's5-5',
          text: 'Threads 每週 3 篇技術內容',
          detail: '調性：職人技術真人秀，不賣咖啡。\n內容：烘焙曲線 / 杯測判斷 / 今天烘什麼豆 / 配方調整過程。\n每篇結尾埋入：「→ 餐廳/辦公室試豆申請」連結',
          done: false,
        },
        {
          id: 's5-6',
          text: 'Wholesale 頁加入 Wholesale Schema（Service）',
          detail: 'Schema type: Service + LocalBusiness。讓 Google 知道：這是 B2B 咖啡豆供應服務，不只是一般咖啡店。',
          done: false,
          tag: '技術',
        },
      ],
      note: '💰 B2B KPI：20 家客戶 × 平均 NT$30,000/月 = NT$600,000/月。這是達到月營收 100 萬的核心引擎。',
      noteType: 'warning',
      expanded: false,
    },
  ]

  const allItems = sections.flatMap(s => s.items)
  const doneCount = allItems.filter(i => checks[i.id]).length
  const totalCount = allItems.length
  const progressPct = Math.round((doneCount / totalCount) * 100)

  const tagColors: Record<string, string> = {
    '關鍵': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    '技術': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    '內容': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    '結構': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    '內鏈': 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
    '優先': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 pt-24 pb-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs tracking-[0.3em] uppercase text-zinc-400 dark:text-zinc-500">ONGU Studio</span>
            <span className="text-zinc-300 dark:text-zinc-700">／</span>
            <span className="text-xs tracking-[0.3em] uppercase text-zinc-400 dark:text-zinc-500">2026 Marketing Plan</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extralight tracking-wide text-zinc-900 dark:text-zinc-100 mb-3">
            2026 行銷作戰計畫
          </h1>
          <p className="text-zinc-500 dark:text-zinc-400 font-light text-sm leading-relaxed">
            核心目標：讓「咖啡豆」搜尋就看到 ONGU。三個入口同步推進：Google Shopping、AI Overview、SEO 長文。
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-sm p-6 mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-light text-zinc-700 dark:text-zinc-300 tracking-wide">整體進度</span>
            <span className="text-sm font-light text-zinc-900 dark:text-zinc-100">
              <span className="font-normal">{doneCount}</span>
              <span className="text-zinc-400"> / {totalCount} 項完成</span>
            </span>
          </div>
          <div className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-zinc-900 dark:bg-zinc-100 transition-all duration-500 rounded-full"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-3 text-right">
            <span className="text-xs text-zinc-400">{progressPct}%</span>
          </div>
        </div>

        {/* Quick Legend */}
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.entries(tagColors).map(([tag, cls]) => (
            <span key={tag} className={`text-xs px-2 py-0.5 rounded-full font-light ${cls}`}>{tag}</span>
          ))}
          <span className="text-xs px-2 py-0.5 rounded-full font-light bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 ml-auto">
            進度會自動儲存在瀏覽器
          </span>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => {
            const secDone = section.items.filter(i => checks[i.id]).length
            const secTotal = section.items.length
            const isOpen = expanded[section.id]

            return (
              <div
                key={section.id}
                className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-sm overflow-hidden"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full text-left px-6 py-5 flex items-start gap-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-sm bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-600 dark:text-zinc-300">
                    {section.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs tracking-widest uppercase text-zinc-400 dark:text-zinc-500">
                        Step {section.step}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-full font-light">
                        {section.badge}
                      </span>
                    </div>
                    <h2 className="text-base font-light text-zinc-900 dark:text-zinc-100 tracking-wide mb-0.5">
                      {section.title}
                    </h2>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 font-light">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-3 ml-4">
                    <div className="text-right hidden sm:block">
                      <div className="text-xs text-zinc-400 font-light">{secDone}/{secTotal}</div>
                      <div className={`text-xs font-light ${section.timelineColor}`}>{section.timeline}</div>
                    </div>
                    {isOpen ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                  </div>
                </button>

                {/* Section Content */}
                {isOpen && (
                  <div className="px-6 pb-6 border-t border-zinc-100 dark:border-zinc-800">
                    {/* Goal */}
                    <div className="mt-4 mb-5 p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-sm">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400 font-light leading-relaxed">
                        <span className="text-zinc-900 dark:text-zinc-200 font-normal">目標：</span>{section.goal}
                      </p>
                    </div>

                    {/* Checklist Items */}
                    <div className="space-y-3">
                      {section.items.map((item) => {
                        const isDone = !!checks[item.id]
                        return (
                          <div
                            key={item.id}
                            className={`group border rounded-sm transition-all duration-200 ${
                              isDone
                                ? 'border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-800/20'
                                : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'
                            }`}
                          >
                            <button
                              onClick={() => toggle(item.id)}
                              className="w-full text-left px-4 py-3 flex items-start gap-3"
                            >
                              <div className="flex-shrink-0 mt-0.5 text-zinc-400 dark:text-zinc-500">
                                {isDone
                                  ? <CheckCircle2 size={18} className="text-zinc-900 dark:text-zinc-100" />
                                  : <Square size={18} />
                                }
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-sm font-light transition-colors ${
                                    isDone
                                      ? 'line-through text-zinc-400 dark:text-zinc-600'
                                      : 'text-zinc-800 dark:text-zinc-200'
                                  }`}>
                                    {item.text}
                                  </span>
                                  {item.tag && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-light ${tagColors[item.tag] || ''}`}>
                                      {item.tag}
                                    </span>
                                  )}
                                </div>
                                {item.detail && (
                                  <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-500 font-light leading-relaxed whitespace-pre-line">
                                    {item.detail}
                                  </p>
                                )}
                              </div>
                            </button>
                          </div>
                        )
                      })}
                    </div>

                    {/* Note */}
                    {section.note && (
                      <div className={`mt-5 p-4 rounded-sm text-sm font-light leading-relaxed ${
                        section.noteType === 'success'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                          : section.noteType === 'warning'
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                      }`}>
                        {section.note}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Bottom Summary */}
        <div className="mt-12 border-t border-zinc-200 dark:border-zinc-800 pt-10">
          <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-400 dark:text-zinc-500 mb-6">三個入口戰略摘要</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: 'Google Shopping',
                desc: '最快出現在「咖啡豆」第一頁。2週內可達成，不等 SEO。',
                timeline: '本週',
                color: 'border-emerald-300 dark:border-emerald-700',
              },
              {
                label: 'AI Overview',
                desc: 'FAQ 問句改成搜尋型 → prerender 確認 schema 存在 → 有機會被 Google AI 引用在搜尋最上方。',
                timeline: '本週',
                color: 'border-blue-300 dark:border-blue-700',
              },
              {
                label: 'SEO 長文',
                desc: '1 篇 2000 字「咖啡豆推薦」指南 → 全程內鏈 /shop/beans → 長期推動自然排名。',
                timeline: '第2–4週',
                color: 'border-purple-300 dark:border-purple-700',
              },
            ].map((item) => (
              <div key={item.label} className={`border-l-2 ${item.color} pl-4 py-1`}>
                <div className="text-xs text-zinc-400 dark:text-zinc-500 mb-1">{item.timeline}</div>
                <div className="text-sm font-light text-zinc-900 dark:text-zinc-100 mb-1">{item.label}</div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-xs text-zinc-400 dark:text-zinc-600 font-light">
            接下來每次討論到的行銷動作，都會持續更新到這個頁面。
          </div>
        </div>

      </div>
    </div>
  )
}

export default MarketingPlanPage
